<template>
  <div>
    <loading-container v-if="loading"></loading-container>

    <div v-else class="container">
      <div class="row">
        <div class="col-xs-6">
          <help-text>
            <h3>
              Details
            </h3>

            <p>
              Edit the details of the company, including the name, CVR and basic contact info.
            </p>
          </help-text>
        </div>

        <div class="col-xs-18">
          <form @submit.prevent="submit">
            <div class="panel panel-default">
              <div class="panel-heading">
                <h5>
                  Basic Information
                </h5>
              </div>

              <div class="panel-body form-horizontal">
                <static-form-group
                    :value="companyData.legacy_id"
                    label="Legacy ID"
                    help-text="The ID of the company in the legacy system."
                ></static-form-group>

                <text-form-group
                    v-model="companyData.name"
                    name="name"
                    label="Name"
                    help-text="The name of the company."
                    :error="getError('name')"
                ></text-form-group>

                <text-form-group
                    v-model="companyData.cvr"
                    name="cvr"
                    label="CVR"
                    help-text="The company's CVR number."
                    :error="getError('cvr')"
                ></text-form-group>

                <text-form-group
                    v-model="companyData.ean"
                    name="ean"
                    label="EAN"
                    help-text="The company's EAN number."
                    :error="getError('ean')"
                ></text-form-group>

                <email-form-group
                    v-model="companyData.email"
                    name="email"
                    label="Invoice Email"
                    help-text="The company's invoice email."
                    :error="getError('email')"
                ></email-form-group>

                <text-form-group
                    v-model="companyData.phone"
                    name="phone"
                    label="Phone"
                    help-text="The company's phone."
                    :error="getError('phone')"
                ></text-form-group>

                <text-form-group
                    v-model="companyData.website"
                    name="website"
                    label="Website"
                    help-text="The company's website."
                    :error="getError('website')"
                ></text-form-group>

                <static-form-group
                    label="Primary Industry"
                    :value="companyData.industry"
                    help-text="The company's primary industry."
                ></static-form-group>

                <static-form-group
                    label="Secondary Industries"
                    :value="companyData.secondary_industries.join('<br>')"
                    help-text="The company's secondary industries."
                ></static-form-group>

                <text-form-group
                    v-model="companyData.number_of_employees"
                    name="number_of_employees"
                    label="Number Of Employees"
                    help-text="The number of employees in the company."
                    :error="getError('number_of_employees')"
                ></text-form-group>

                <date-form-group
                    v-model="companyData.established_at"
                    name="established_at"
                    label="Established At"
                    help-text="The date that the company was established."
                    :error="getError('established_at')"
                    format="DD.MM.YYYY"
                ></date-form-group>

                <tags-form-group
                    v-model="tags"
                    name="tags"
                    label="Tags"
                    help-text="Any number of tags that describe the company."
                    :error="getError('tags')"
                ></tags-form-group>

                <select-form-group
                    v-if="hasPermission('admin')"
                    v-model="companyData.payment_condition_id"
                    name="payment_condition_id"
                    label="Payment Condition"
                    help-text="The payment condition that will be used for the company's invoices."
                    :error="getError('payment_condition_id')"
                    :options="paymentConditionOptions"
                    :placeholder="'Default (' + defaultPaymentCondition.attributes.name + ')'"
                    :nullable="true"
                ></select-form-group>

                <select-form-group
                    v-model="companyData.advertising_contact_id"
                    name="advertising_contact_id"
                    label="Advertising Contact"
                    help-text="The advertising contact in the company."
                    :error="getError('advertising_contact_id')"
                    :options="peopleOptions"
                    :nullable="true"
                ></select-form-group>

                <select-form-group
                    v-model="companyData.subscription_contact_id"
                    name="subscription_contact_id"
                    label="Subscription Contact"
                    help-text="The subscription contact in the company."
                    :error="getError('subscription_contact_id')"
                    :options="peopleOptions"
                    :nullable="true"
                ></select-form-group>
              </div>
            </div>

            <address-panel
                v-model="companyData.address"
                :countries="countries"
                :errors="errors"
            ></address-panel>

            <hr class="panel-separator">

            <button type="submit" class="btn btn-black" style="margin-bottom: 22px;">
              <i class="fa fa-save"></i> Save
            </button>
          </form>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import http from '@/http'
import notify from '@/notify'
import AddressPanel from '@/components/AddressPanel'
import DateFormGroup from '@/vendor/forms/FormGroups/Horizontal/Date'
import EmailFormGroup from '@/vendor/forms/FormGroups/Horizontal/Email'
import Errors from '@/mixins/Errors'
import GalleryFormGroup from '@/components/FormGroups/Horizontal/Gallery'
import HelpText from '@/components/HelpText'
import Load from '@/mixins/Load'
import LoadingContainer from '@/components/Layout/LoadingContainer'
import Permissions from '@/mixins/Permissions'
import RadioFormGroup from '@/vendor/forms/FormGroups/Horizontal/Radio'
import SelectFormGroup from '@/vendor/forms/FormGroups/Horizontal/Select'
import StaticFormGroup from '@/vendor/forms/FormGroups/Horizontal/Static'
import SubmitFormGroup from '@/vendor/forms/FormGroups/Horizontal/Submit'
import TagsFormGroup from '@/components/FormGroups/Horizontal/Tags'
import TextFormGroup from '@/vendor/forms/FormGroups/Horizontal/Text'
import TextareaFormGroup from '@/vendor/forms/FormGroups/Horizontal/Textarea'

export default {
  components: {
    AddressPanel,
    DateFormGroup,
    EmailFormGroup,
    GalleryFormGroup,
    HelpText,
    LoadingContainer,
    RadioFormGroup,
    SelectFormGroup,
    StaticFormGroup,
    SubmitFormGroup,
    TagsFormGroup,
    TextFormGroup,
    TextareaFormGroup,
  },

  mixins: [Errors, Load, Permissions],

  model: {
    prop: 'company',
    event: 'company-updated'
  },

  props: ['company'],

  data() {
    return {
      paymentConditions: [],
      countries: [],
      people: [],
      users: [],
      companyData: _.cloneDeep(this.company.data),
      tags: this.company.data.tags.map(tag => {
        return {text: tag.name, style: 'background-color:' + tag.color + ';color:#ffffff'}
      }),
      showDisplayInformation: false
    }
  },

  mounted() {
    this.load({
      paymentConditions: '/api/v2/payment-conditions',
      countries: '/api/v2/countries',
      people: '/api/v1/companies/' + this.company.data.id + '/people',
      users: '/api/v2/users'
    })
  },

  computed: {
    paymentConditionOptions() {
      return this.paymentConditions.data.map(paymentCondition => {
        return {
          value: paymentCondition.id,
          label: paymentCondition.attributes.name
        }
      })
    },

    peopleOptions() {
      return this.people.data.map(person => {
        return {
          value: person.id,
          label: person.name
        }
      })
    },

    userOptions() {
      return this.users.data.map(user => {
        return {
          value: user.id,
          label: user.attributes.full_name
        }
      })
    },

    defaultPaymentCondition() {
      return this.paymentConditions.data.find(paymentCondition => paymentCondition.attributes.is_default)
    },

    requestData() {
      return {
        ...this.companyData,
        tags: this.tags.map(tag => tag.text)
      }
    }
  },

  methods: {
    submit() {
      http.put('/api/v1/companies/' + this.company.data.id, this.requestData)
          .then(response => {
            notify.success('The company was saved')

            this.errors = {}

            this.$emit('company-updated', response.data)
          })
          .catch(error => {
            if (error.response.status === 400) {
              return notify.error(error.response.data.message)
            }

            throw error
          })
          .catch(this.catchValidationErrors)
    }
  }
}
</script>
